import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api, { BACKEND_URL, getErrorMessage } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, DollarSign, Plus, FileText, Upload, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

export default function InstructorHoursPage() {
  const { user } = useAuth();
  const [hours, setHours] = useState([]);
  const [funding, setFunding] = useState([]);
  const [tab, setTab] = useState('hours');
  const [showLogHour, setShowLogHour] = useState(false);
  const [showFundingReq, setShowFundingReq] = useState(false);
  const [hourForm, setHourForm] = useState({ hours: 1, reason: '' });
  const [fundingForm, setFundingForm] = useState({ description: '', amount: '', child_name: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [h, f] = await Promise.all([api.get('/hours'), api.get('/funding')]);
      setHours(h.data);
      setFunding(f.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  };

  const submitHourRequest = async () => {
    if (!hourForm.reason.trim()) { toast.error('Please provide a reason'); return; }
    try {
      await api.post('/hours', { hours: hourForm.hours, reason: hourForm.reason, type: 'manual' });
      toast.success('Hour request submitted for admin approval');
      setShowLogHour(false);
      setHourForm({ hours: 1, reason: '' });
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err, 'Failed')); }
  };

  const submitFundingRequest = async () => {
    const amount = parseFloat(fundingForm.amount);
    if (!fundingForm.description.trim()) { toast.error('Please describe what you need'); return; }
    if (!amount || amount <= 0) { toast.error('Please enter a valid amount'); return; }
    if (amount > 150) { toast.error('Maximum funding request is $150'); return; }
    try {
      await api.post('/funding', { description: fundingForm.description, amount, child_name: fundingForm.child_name });
      toast.success('Funding request submitted');
      setShowFundingReq(false);
      setFundingForm({ description: '', amount: '', child_name: '' });
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err, 'Failed')); }
  };

  const uploadReceipt = async (requestId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/funding/${requestId}/receipt`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Receipt uploaded!');
      fetchData();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const approvedHours = hours.filter(h => h.status === 'approved').reduce((sum, h) => sum + h.hours, 0);
  const pendingHours = hours.filter(h => h.status === 'pending').reduce((sum, h) => sum + h.hours, 0);
  const payRate = user?.pay_rate || 37.5;

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn" data-testid="instructor-hours-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>Hours & Funding</h1>
          <p className="text-slate-500 mt-1">Track your hours and manage funding requests</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="log-hour-btn" onClick={() => setShowLogHour(true)} className="rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-white font-bold gap-2">
            <Plus className="w-4 h-4" /> Log Hours
          </Button>
          <Button data-testid="request-funding-btn" onClick={() => setShowFundingReq(true)} variant="outline" className="rounded-full border-2 border-[#C4F017] text-[#1E1B4B] font-bold gap-2 hover:bg-[#F7FEE7]">
            <DollarSign className="w-4 h-4" /> Request Funding
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-extrabold text-green-700" style={{ fontFamily: 'Nunito' }}>{approvedHours}</p>
            <p className="text-xs text-slate-500 font-medium">Approved Hours</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-extrabold text-amber-600" style={{ fontFamily: 'Nunito' }}>{pendingHours}</p>
            <p className="text-xs text-slate-500 font-medium">Pending Hours</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-extrabold text-[#0E7490]" style={{ fontFamily: 'Nunito' }}>${(approvedHours * payRate).toFixed(2)}</p>
            <p className="text-xs text-slate-500 font-medium">Total Gross Earnings</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab('hours')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm ${tab === 'hours' ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-600'}`}>
          <Clock className="w-4 h-4" /> Hours Log
        </button>
        <button onClick={() => setTab('funding')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm ${tab === 'funding' ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-600'}`}>
          <DollarSign className="w-4 h-4" /> Funding Requests
        </button>
      </div>

      {/* HOURS TAB */}
      {tab === 'hours' && (
        <div className="space-y-3">
          {hours.length === 0 && (
            <Card className="rounded-3xl border-dashed border-2 border-slate-200">
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-lg font-bold text-slate-400">No hours logged yet</p>
                <p className="text-sm text-slate-400">Complete a session or log extra hours above</p>
              </CardContent>
            </Card>
          )}
          {hours.map(h => (
            <Card key={h.log_id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`hour-log-${h.log_id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${h.type === 'session' ? 'bg-[#ECFEFF] text-[#0E7490]' : 'bg-amber-50 text-amber-600'}`}>
                    {h.type === 'session' ? <CheckCircle2 className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-700 text-sm">{h.reason}</p>
                    <p className="text-xs text-slate-400">{new Date(h.created_at).toLocaleDateString()} &middot; {h.hours}hr &middot; {h.type === 'session' ? 'Auto-logged' : 'Manual request'}</p>
                    {h.admin_notes && <p className="text-xs text-slate-500 mt-1">Admin: {h.admin_notes}</p>}
                  </div>
                </div>
                <Badge variant="outline" className={`rounded-full ${h.status === 'approved' ? 'border-green-200 text-green-600 bg-green-50' : h.status === 'rejected' ? 'border-red-200 text-red-600 bg-red-50' : 'border-amber-200 text-amber-600 bg-amber-50'}`}>{h.status}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* FUNDING TAB */}
      {tab === 'funding' && (
        <div className="space-y-3">
          {funding.length === 0 && (
            <Card className="rounded-3xl border-dashed border-2 border-slate-200">
              <CardContent className="py-12 text-center">
                <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-lg font-bold text-slate-400">No funding requests</p>
                <p className="text-sm text-slate-400">Request funding for equipment or tools</p>
              </CardContent>
            </Card>
          )}
          {funding.map(f => (
            <Card key={f.request_id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`funding-req-${f.request_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-slate-700">{f.description}</p>
                    <p className="text-xs text-slate-400">{new Date(f.created_at).toLocaleDateString()} {f.child_name ? `for ${f.child_name}` : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-extrabold text-[#1E1B4B]">${f.amount.toFixed(2)}</p>
                    <Badge variant="outline" className={`rounded-full ${f.status === 'approved' ? 'border-green-200 text-green-600 bg-green-50' : f.status === 'rejected' ? 'border-red-200 text-red-600 bg-red-50' : f.status === 'completed' ? 'border-blue-200 text-blue-600 bg-blue-50' : 'border-amber-200 text-amber-600 bg-amber-50'}`}>{f.status}</Badge>
                  </div>
                </div>
                {f.admin_notes && <p className="text-xs text-slate-500 mb-2">Admin note: {f.admin_notes}</p>}

                {/* Upload receipt after approved */}
                {f.status === 'approved' && !f.receipt_filename && (
                  <div className="mt-3 p-3 bg-[#F7FEE7] rounded-xl">
                    <p className="text-xs font-semibold text-[#1E1B4B] mb-2">Approved! Upload your receipt for reimbursement:</p>
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-[#C4F017] text-sm font-bold text-[#1E1B4B] cursor-pointer hover:bg-[#ECFCCB] transition-colors" data-testid={`upload-receipt-${f.request_id}`}>
                      <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload Receipt'}
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => uploadReceipt(f.request_id, e)} />
                    </label>
                  </div>
                )}
                {f.receipt_filename && (
                  <div className="mt-2 flex items-center gap-2 text-green-600">
                    <FileText className="w-4 h-4" />
                    <span className="text-xs font-semibold">Receipt uploaded — reimbursement on next paycheck</span>
                  </div>
                )}

                {f.status === 'pending' && (
                  <div className="mt-2 flex items-center gap-2 text-amber-500">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs">Awaiting admin approval</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Log Hours Dialog */}
      <Dialog open={showLogHour} onOpenChange={setShowLogHour}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Request Additional Hours</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-500">Session hours are auto-logged. Use this to request extra hours (meetings, prep time, etc.)</p>
            <div>
              <Label className="font-semibold text-slate-600">Hours</Label>
              <Input data-testid="extra-hours-input" className="mt-1 rounded-xl" type="number" min="0.5" step="0.5" value={hourForm.hours} onChange={e => setHourForm({ ...hourForm, hours: parseFloat(e.target.value) || 1 })} />
            </div>
            <div>
              <Label className="font-semibold text-slate-600">Reason</Label>
              <Textarea data-testid="hour-reason-input" className="mt-1 rounded-xl" placeholder="e.g., Parent meeting for Alex, session prep, training..." value={hourForm.reason} onChange={e => setHourForm({ ...hourForm, reason: e.target.value })} rows={3} />
            </div>
            <Button data-testid="submit-hour-request-btn" onClick={submitHourRequest} className="w-full rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] font-bold">Submit Request</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Funding Request Dialog */}
      <Dialog open={showFundingReq} onOpenChange={setShowFundingReq}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Request Funding</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-500">Request up to $150 for equipment, tools, or teaching materials</p>
            <div>
              <Label className="font-semibold text-slate-600">What do you need?</Label>
              <Textarea data-testid="funding-desc-input" className="mt-1 rounded-xl" placeholder="e.g., Kickboard set, goggles, sensory toys..." value={fundingForm.description} onChange={e => setFundingForm({ ...fundingForm, description: e.target.value })} rows={3} />
            </div>
            <div>
              <Label className="font-semibold text-slate-600">Amount ($)</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input data-testid="funding-amount-input" className="pl-10 rounded-xl" type="number" min="1" max="150" step="0.01" placeholder="0.00" value={fundingForm.amount} onChange={e => setFundingForm({ ...fundingForm, amount: e.target.value })} />
              </div>
              <p className="text-xs text-slate-400 mt-1">Maximum: $150.00</p>
            </div>
            <div>
              <Label className="font-semibold text-slate-600">For which student? (optional)</Label>
              <Input data-testid="funding-child-input" className="mt-1 rounded-xl" placeholder="Student name" value={fundingForm.child_name} onChange={e => setFundingForm({ ...fundingForm, child_name: e.target.value })} />
            </div>
            <Button data-testid="submit-funding-btn" onClick={submitFundingRequest} className="w-full rounded-full bg-[#C4F017] hover:bg-[#A3D013] text-[#1E1B4B] font-bold">Submit Request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
